/**
 * Holds the keys used in the local storage cache.
 */
import logger from './logging';
import LZString from "lz-string";

export const LOCAL_STORAGE_KEYS = Object.freeze({
  // Changing to V2 because there is difference between what is cached since cachedGet's introduction
  // By changing version, we can simply ignore existing key and create a fresh item
  GL_PRODUCT_GROUPS: 'GL_PRODUCT_GROUPS_V2',

  PRODUCT_LINES: 'PRODUCT_LINES',
  CHANNEL_MAPPINGS: 'CHANNEL_MAPPINGS',
  SAVED_REPORTS: 'SAVED_REPORTS',
  SNAPSHOTS: 'SNAPSHOTS',

  CONFIG: 'CONFIG',
  COLUMN_CONFIG: 'COLUMN_CONFIG',
  COMPONENT_CONFIG: 'COMPONENT_CONFIG',
  HIERARCHY_CONFIG: 'HIERARCHY_CONFIG',
  MARKETPLACE_CONFIG: 'MARKETPLACE_CONFIG',
  CHANNEL_TREND_GOAL_CONFIG: 'CHANNEL_TREND_GOAL_CONFIG',
  COMPONENT_TREND_GOAL_CONFIG: 'COMPONENT_TREND_GOAL_CONFIG',
  AUTO_BENCHMARK_ADJUSTMENT_TESTING_COMPONENTS: 'AUTO_BENCHMARK_ADJUSTMENT_TESTING_COMPONENTS',

  IDENTITY: 'IDENTITY',
  FEATURE_ACCESS: 'FEATURE_ACCESS',

  NEW_FEATURE_DISPLAY_ITEMS: 'NEW_FEATURE_DISPLAY_ITEMS',
});

export const IGNORE_COMPRESSION_KEYS = [
  LOCAL_STORAGE_KEYS.IDENTITY
]

const EXPIRY_IN_DAYS = 30;
const LARGE_OBJECT_SIZE_LIMIT = 8 * 1024 * 1024;

/**
 * Loads a value from the local storage. If the item
 * has expired - will return null.
 *
 * @param {string} key The key to lookup the item.
 */
export const loadFromLocalStorage = (key) => {
  if (hasExpired(key)) {
    delete localStorage[key];
    return null;
  }
  try {
    let value = localStorage[key];
    if (value) {
      let obj = IGNORE_COMPRESSION_KEYS.includes(key) ? value.data : JSON.parse(LZString.decompress(value)).data;
      logger.info(`Found cached result for ${key}`);
      return obj;
    }
  } catch (err) {
    logger.error(`Error fetching value from localstorage probably uncompressed, clearing the key: ${key}`)
    delete localStorage[key];
    return null;
  }
};

/**
 * Delete a value from the local storage. Irrespective of whether the item is expired or not.
 *
 * @param {string} key The key to lookup the item.
 */
export const deleteFromLocalStorage = (key) => {
  try {
    delete localStorage[key];
    logger.debug(`Successfully deleted from local storage: ${key}`);
  } catch (e) {
    logger.error(`Error deleting from local storage: ${ key }`, e);
  }
};

/**
 * Saves an item to the local storage along with the current time.
 * The time is saved to check if the key has expired when loading the
 * item from local storage cache.
 *
 * The supplied key can be anything but preferred to be the one already defined in LOCAL_STORAGE_KEYS
 *
 * @param {string} key The key to store the item.
 * @param {*} data The data to store.
 */
export const saveToLocalStorage = (key, data) => {
  let value = JSON.stringify({ data, lastStored: new Date() });
  if (!IGNORE_COMPRESSION_KEYS.includes(key)) {
    value = LZString.compress(value);
  }

  if (value.length > LARGE_OBJECT_SIZE_LIMIT) {
    logger.warn(`Object too large to store it in local storage, skipping it: ${key}`);
    return ;
  }

  if (key && data) {
    try {
      localStorage[key] = value;
      logger.debug(`Successfully stored in localStorage: ${key}`);
    } catch (err) {
        logger.warn(`Error saving to local storage for the Key: ${key}`, {}, err)
        removeOlderOrLargeKeys(3);
    }
  } else {
    logger.error(`Invalid key or data: Key: ${ key }, data: ${ data }`);
  }
};

const removeOlderOrLargeKeys = (days) => {
  logger.info(`Removing keys older than ${days} days`);
  let keys = Object.keys(localStorage);
  let keysCountBeforeDeletion = keys.length;
  try {
    let expiredKeys = keys
          .filter(key => key !== LOCAL_STORAGE_KEYS.NEW_FEATURE_DISPLAY_ITEMS)
          .filter(key => hasExpired(key, days) || localStorage[key] && localStorage[key].length >= LARGE_OBJECT_SIZE_LIMIT)
          .map(key => deleteFromLocalStorage(key));
  } catch (e) {
    logger.error(`Error removing older/large keys`, e);
  }

  return keysCountBeforeDeletion - Object.keys(localStorage).length;
};

const hasExpired = (key, days = EXPIRY_IN_DAYS) => {
  const jsonValue = localStorage[key];
  try {
    const value = jsonValue ? JSON.parse(jsonValue) : null;
    return !value || days < dateDiffInDays(value.lastStored, new Date());
  } catch (e) {
    // Either non json from other web application components or doesn't have lastStored attribute
    // Need to ignore those items from local storage
    return false;
  }
};

const dateDiffInDays = (date1, date2) => {
  const dt1 = new Date(date1);
  const dt2 = new Date(date2);
  return Math.floor(
    (Date.UTC(dt2.getFullYear(), dt2.getMonth(), dt2.getDate()) -
      Date.UTC(dt1.getFullYear(), dt1.getMonth(), dt1.getDate())) /
      (1000 * 60 * 60 * 24)
  );
};



// WEBPACK FOOTER //
// ./src/utils/local_storage.js